/**
 * Shield Bearer (盾兵) - Gold/brown heavy armor, full helmet, tower shield
 * Heavy defensive infantry
 */

import * as THREE from 'three';
import { COLORS } from '../unitConstants';
import { createChineseHelmet, createArm, createLeg, mergeGeometries } from '../unitHelpers';

export function createShieldBearerGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const colors: THREE.Color[] = [];

  // === HEAD ===
  const head = new THREE.SphereGeometry(0.09, 8, 6);
  head.translate(0, 0.46, 0);
  parts.push(head);
  colors.push(COLORS.skin);

  // Full helmet with face mask
  createChineseHelmet(parts, colors, 0.52, {
    hasSpike: true,
    hasTassel: false,
    hasNeckGuard: true,
    hasFaceMask: true,
    spikeHeight: 0.04,
    helmetColor: COLORS.shieldBrown,
  });

  // === TORSO (Heavy armor - stocky build) ===
  // Wider chest for defensive stance
  const chest = new THREE.CylinderGeometry(0.10, 0.12, 0.12, 8);
  chest.translate(0, 0.33, 0);
  parts.push(chest);
  colors.push(COLORS.shieldBrown);

  // Front chest armor plate
  const chestPlate = new THREE.BoxGeometry(0.14, 0.10, 0.025);
  chestPlate.translate(0, 0.33, 0.10);
  parts.push(chestPlate);
  colors.push(COLORS.bronze);

  // 护心镜（中国古代风格 - 圆形铜镜）
  const heartMirror = new THREE.CylinderGeometry(0.04, 0.04, 0.012, 12);
  heartMirror.rotateX(Math.PI / 2);
  heartMirror.translate(0, 0.33, 0.115);
  parts.push(heartMirror);
  colors.push(COLORS.gold);

  // 护心镜外圈装饰
  const heartMirrorRing = new THREE.TorusGeometry(0.04, 0.008, 6, 16);
  heartMirrorRing.rotateX(Math.PI / 2);
  heartMirrorRing.translate(0, 0.33, 0.115);
  parts.push(heartMirrorRing);
  colors.push(COLORS.bronze);

  // 护心镜中心宝珠
  const heartGem = new THREE.SphereGeometry(0.015, 6, 5);
  heartGem.translate(0, 0.33, 0.125);
  parts.push(heartGem);
  colors.push(COLORS.gold);

  // Heavy shoulder pauldrons
  for (const side of [-1, 1]) {
    const pauldron = new THREE.SphereGeometry(
      0.05,
      6,
      5,
      0,
      Math.PI * 2,
      0,
      Math.PI * 0.6
    );
    pauldron.rotateZ(side * 0.35);
    pauldron.translate(side * 0.13, 0.37, 0);
    parts.push(pauldron);
    colors.push(COLORS.shieldBrown);

    const edge = new THREE.TorusGeometry(0.035, 0.007, 4, 8, Math.PI);
    edge.rotateY(Math.PI / 2);
    edge.rotateZ(side * 0.35);
    edge.translate(side * 0.14, 0.34, 0);
    parts.push(edge);
    colors.push(COLORS.gold);
  }

  // Belt
  const belt = new THREE.CylinderGeometry(0.125, 0.125, 0.028, 8);
  belt.translate(0, 0.25, 0);
  parts.push(belt);
  colors.push(COLORS.gold);

  // Belt buckle
  const buckle = new THREE.BoxGeometry(0.04, 0.028, 0.015);
  buckle.translate(0, 0.25, 0.12);
  parts.push(buckle);
  colors.push(COLORS.gold);

  // Armored skirt (shorter for mobility)
  const skirt = new THREE.CylinderGeometry(0.12, 0.14, 0.07, 8);
  skirt.translate(0, 0.19, 0);
  parts.push(skirt);
  colors.push(COLORS.shieldBrown);

  // === ARMS (thicker, armored) ===
  const shoulderY = 0.35;

  // Left arm (holding shield - bent to front)
  createArm(
    parts,
    colors,
    -0.12,
    shoulderY,
    COLORS.shieldBrown,
    COLORS.skin,
    0.8,
    0.6
  );

  // Right arm (holding sword - relaxed)
  createArm(
    parts,
    colors,
    0.12,
    shoulderY,
    COLORS.shieldBrown,
    COLORS.skin,
    -0.4,
    -0.3
  );

  // === LEGS (stocky, armored) ===
  const hipY = 0.15;
  createLeg(
    parts,
    colors,
    -0.045,
    hipY,
    COLORS.shieldBrown,
    COLORS.iron,
    0.06
  );
  createLeg(
    parts,
    colors,
    0.045,
    hipY,
    COLORS.shieldBrown,
    COLORS.iron,
    -0.06
  );

  // Knee guards
  for (const xOff of [-0.045, 0.045]) {
    const kneeGuard = new THREE.SphereGeometry(
      0.022,
      5,
      4,
      0,
      Math.PI * 2,
      0,
      Math.PI * 0.5
    );
    kneeGuard.rotateX(-Math.PI / 2);
    kneeGuard.translate(xOff, 0.08, 0.028);
    parts.push(kneeGuard);
    colors.push(COLORS.bronze);
  }

  // === WEAPON: Large Tower Shield (大盾) ===
  // 计算左手实际位置（基于 createArm 参数）
  const leftArmAngle = 0.8;
  const leftForearmAngle = 0.6;
  const leftTotalAngle = leftArmAngle + leftForearmAngle;
  const leftArmLength = 0.08;
  const leftForearmLength = 0.07;
  const leftXOffset = -0.12;

  const leftHandX =
    leftXOffset +
    Math.sin(leftArmAngle) * leftArmLength +
    Math.sin(leftTotalAngle) * leftForearmLength;
  const leftHandY =
    shoulderY -
    Math.cos(leftArmAngle) * leftArmLength -
    Math.cos(leftTotalAngle) * leftForearmLength;

  // 盾牌位置：在身体前方，不嵌入身体
  const shieldX = leftHandX - 0.02; // 盾牌中心稍微偏左
  const shieldZ = 0.16; // 在身体前方

  // 左手前臂延伸到盾牌位置（连接手和盾牌）
  const forearmExtension = new THREE.CylinderGeometry(0.02, 0.018, 0.12, 6);
  forearmExtension.rotateX(Math.PI / 2.5);
  forearmExtension.translate(leftHandX - 0.01, leftHandY + 0.02, 0.07);
  parts.push(forearmExtension);
  colors.push(COLORS.shieldBrown);

  // 盾牌握把（在盾牌背面）
  const shieldGripHorizontal = new THREE.CylinderGeometry(0.012, 0.012, 0.08, 6);
  shieldGripHorizontal.rotateZ(Math.PI / 2);
  shieldGripHorizontal.translate(shieldX, 0.30, shieldZ - 0.03);
  parts.push(shieldGripHorizontal);
  colors.push(COLORS.wood);

  // 握把支架（连接盾面和握把）
  for (const xOff of [-0.035, 0.035]) {
    const gripMount = new THREE.BoxGeometry(0.015, 0.025, 0.025);
    gripMount.translate(shieldX + xOff, 0.30, shieldZ - 0.018);
    parts.push(gripMount);
    colors.push(COLORS.iron);
  }

  // 左手握住握把（用棕色手套，更明显）
  const leftGloveColor = COLORS.woodDark;
  const leftGripHand = new THREE.SphereGeometry(0.028, 6, 5);
  leftGripHand.translate(shieldX, 0.30, shieldZ - 0.038);
  parts.push(leftGripHand);
  colors.push(leftGloveColor);

  // 左手手指环绕握把（棕色手套）
  for (let i = 0; i < 4; i++) {
    const fingerAngle = (i / 4) * Math.PI - Math.PI * 0.5;
    const finger = new THREE.CylinderGeometry(0.009, 0.008, 0.028, 4);
    finger.rotateZ(Math.PI / 2);
    finger.translate(
      shieldX,
      0.30 + Math.sin(fingerAngle) * 0.02,
      shieldZ - 0.03 + Math.cos(fingerAngle) * 0.02
    );
    parts.push(finger);
    colors.push(leftGloveColor);
  }

  // Shield body (large, rectangular with rounded top)
  const shieldBody = new THREE.BoxGeometry(0.26, 0.35, 0.025);
  shieldBody.translate(shieldX, 0.30, shieldZ);
  parts.push(shieldBody);
  colors.push(COLORS.shieldBrown);

  // Shield top curve
  const shieldTop = new THREE.CylinderGeometry(
    0.13,
    0.13,
    0.025,
    8,
    1,
    false,
    0,
    Math.PI
  );
  shieldTop.rotateX(Math.PI / 2);
  shieldTop.rotateZ(Math.PI / 2);
  shieldTop.translate(shieldX, 0.475, shieldZ);
  parts.push(shieldTop);
  colors.push(COLORS.shieldBrown);

  // Shield rim (metal edge)
  const rimTop = new THREE.BoxGeometry(0.28, 0.018, 0.03);
  rimTop.translate(shieldX, 0.475, shieldZ);
  parts.push(rimTop);
  colors.push(COLORS.gold);

  for (const xOff of [-0.13, 0.13]) {
    const rimSide = new THREE.BoxGeometry(0.018, 0.35, 0.03);
    rimSide.translate(shieldX + xOff, 0.30, shieldZ);
    parts.push(rimSide);
    colors.push(COLORS.gold);
  }

  const rimBottom = new THREE.BoxGeometry(0.28, 0.018, 0.03);
  rimBottom.translate(shieldX, 0.125, shieldZ);
  parts.push(rimBottom);
  colors.push(COLORS.gold);

  // Shield boss (center dome)
  const boss = new THREE.SphereGeometry(
    0.05,
    8,
    5,
    0,
    Math.PI * 2,
    0,
    Math.PI * 0.5
  );
  boss.rotateX(-Math.PI / 2);
  boss.translate(shieldX, 0.32, shieldZ + 0.02);
  parts.push(boss);
  colors.push(COLORS.gold);

  // 盾面虎头图案（中国古代风格）
  // 虎头主体（圆形）
  const tigerHead = new THREE.CylinderGeometry(0.055, 0.055, 0.01, 12);
  tigerHead.rotateX(Math.PI / 2);
  tigerHead.translate(shieldX, 0.32, shieldZ + 0.015);
  parts.push(tigerHead);
  colors.push(COLORS.gold);

  // 虎眼（左右两个）
  const tigerEyeL = new THREE.SphereGeometry(0.012, 6, 5);
  tigerEyeL.translate(shieldX - 0.03, 0.34, shieldZ + 0.02);
  parts.push(tigerEyeL);
  colors.push(COLORS.bronze);

  const tigerEyeR = new THREE.SphereGeometry(0.012, 6, 5);
  tigerEyeR.translate(shieldX + 0.03, 0.34, shieldZ + 0.02);
  parts.push(tigerEyeR);
  colors.push(COLORS.bronze);

  // 虎鼻
  const tigerNose = new THREE.BoxGeometry(0.02, 0.015, 0.008);
  tigerNose.translate(shieldX, 0.31, shieldZ + 0.025);
  parts.push(tigerNose);
  colors.push(COLORS.bronze);

  // 虎耳（左右两个三角形）
  const tigerEarL = new THREE.ConeGeometry(0.018, 0.03, 3);
  tigerEarL.translate(shieldX - 0.045, 0.375, shieldZ + 0.015);
  parts.push(tigerEarL);
  colors.push(COLORS.gold);

  const tigerEarR = new THREE.ConeGeometry(0.018, 0.03, 3);
  tigerEarR.translate(shieldX + 0.045, 0.375, shieldZ + 0.015);
  parts.push(tigerEarR);
  colors.push(COLORS.gold);

  // 虎须（左右各两条）
  const whiskerL1 = new THREE.BoxGeometry(0.035, 0.004, 0.004);
  whiskerL1.rotateZ(0.2);
  whiskerL1.translate(shieldX - 0.04, 0.30, shieldZ + 0.025);
  parts.push(whiskerL1);
  colors.push(COLORS.bronze);

  const whiskerL2 = new THREE.BoxGeometry(0.035, 0.004, 0.004);
  whiskerL2.rotateZ(-0.2);
  whiskerL2.translate(shieldX - 0.04, 0.28, shieldZ + 0.025);
  parts.push(whiskerL2);
  colors.push(COLORS.bronze);

  const whiskerR1 = new THREE.BoxGeometry(0.035, 0.004, 0.004);
  whiskerR1.rotateZ(-0.2);
  whiskerR1.translate(shieldX + 0.04, 0.30, shieldZ + 0.025);
  parts.push(whiskerR1);
  colors.push(COLORS.bronze);

  const whiskerR2 = new THREE.BoxGeometry(0.035, 0.004, 0.004);
  whiskerR2.rotateZ(0.2);
  whiskerR2.translate(shieldX + 0.04, 0.28, shieldZ + 0.025);
  parts.push(whiskerR2);
  colors.push(COLORS.bronze);

  // === WEAPON: 长枪（中国古代风格） ===
  // 计算右手位置（基于 createArm 参数）
  const armAngle = -0.4;
  const forearmAngle = -0.3;
  const totalAngle = armAngle + forearmAngle;
  const armLength = 0.08;
  const forearmLength = 0.07;
  const xOffset = 0.12;
  const yBase = shoulderY; // 0.35

  const rightHandX =
    xOffset +
    Math.sin(armAngle) * armLength +
    Math.sin(totalAngle) * forearmLength;
  const rightHandY =
    yBase -
    Math.cos(armAngle) * armLength -
    Math.cos(totalAngle) * forearmLength;

  // 长枪位置：在身体右侧远离身体
  const spearX = 0.18; // 在身体右侧外面
  const spearZ = 0.08; // 稍微在前面

  // 右手前臂延伸到枪的位置（从手臂末端到枪）
  const rightForearmExt = new THREE.CylinderGeometry(0.02, 0.018, 0.10, 6);
  rightForearmExt.rotateZ(-0.3);
  rightForearmExt.rotateX(Math.PI / 4);
  rightForearmExt.translate(rightHandX + 0.03, rightHandY, 0.04);
  parts.push(rightForearmExt);
  colors.push(COLORS.shieldBrown);

  // 枪杆（木质，垂直向上）
  const spearShaft = new THREE.CylinderGeometry(0.012, 0.014, 0.55, 6);
  spearShaft.translate(spearX, rightHandY + 0.22, spearZ);
  parts.push(spearShaft);
  colors.push(COLORS.wood);

  // 枪杆底部（尾端，略粗）
  const spearButt = new THREE.CylinderGeometry(0.016, 0.014, 0.06, 6);
  spearButt.translate(spearX, rightHandY - 0.08, spearZ);
  parts.push(spearButt);
  colors.push(COLORS.woodDark);

  // 枪尾铁箍
  const buttCap = new THREE.CylinderGeometry(0.018, 0.018, 0.015, 8);
  buttCap.translate(spearX, rightHandY - 0.115, spearZ);
  parts.push(buttCap);
  colors.push(COLORS.iron);

  // 枪头底座（金属套筒）
  const spearCollar = new THREE.CylinderGeometry(0.02, 0.016, 0.04, 8);
  spearCollar.translate(spearX, rightHandY + 0.48, spearZ);
  parts.push(spearCollar);
  colors.push(COLORS.bronze);

  // 枪头（三棱形矛头）
  const spearHead = new THREE.ConeGeometry(0.025, 0.12, 3);
  spearHead.translate(spearX, rightHandY + 0.56, spearZ);
  parts.push(spearHead);
  colors.push(COLORS.steel);

  // 红缨（枪头下的装饰）
  const tassel = new THREE.CylinderGeometry(0.035, 0.01, 0.04, 8);
  tassel.translate(spearX, rightHandY + 0.45, spearZ);
  parts.push(tassel);
  colors.push(COLORS.tassel);

  // 右手握枪（用棕色手套，更明显）
  const gloveColor = COLORS.woodDark;
  const rightGripHand = new THREE.SphereGeometry(0.025, 6, 5);
  rightGripHand.translate(spearX, rightHandY, spearZ);
  parts.push(rightGripHand);
  colors.push(gloveColor);

  // 握枪的手指环绕枪杆（棕色手套）
  for (let i = 0; i < 4; i++) {
    const fingerAngle = (i / 4) * Math.PI + Math.PI * 0.25;
    const finger = new THREE.CylinderGeometry(0.009, 0.008, 0.032, 4);
    finger.rotateX(Math.PI / 2);
    finger.rotateY(fingerAngle);
    finger.translate(
      spearX + Math.cos(fingerAngle) * 0.02,
      rightHandY,
      spearZ + Math.sin(fingerAngle) * 0.02
    );
    parts.push(finger);
    colors.push(gloveColor);
  }

  return mergeGeometries(parts, colors);
}
